<template>
    <div>
        <!-- 반응형 여백 -->
        <template v-if="width < 2054 && width > 1699">
            <vs-row justify="flex-end">
                <vs-col :key="index" v-for="(col, index) in 3" w="2">
                    <br />
                </vs-col>
            </vs-row>
        </template>

        <vx-card>
            <!-- 탭 네비게이션 -->
            <TodoTabNavigation 
                :active-tab="activeTab"
                @tab-change="handleTabChange"
            />

            <!-- 날짜 네비게이션 -->
            <DateRangeNavigation 
                :display-range="displayDateRange"
                :show-navigation="showDateNavigation"
                :is-loading="isLoading"
                :is-today="isToday"
                @prev-period="handlePrevPeriod"
                @next-period="handleNextPeriod"
                @go-today="handleGoToday"
            />

            <!-- 상담 담당자 필터 -->
            <CounselorFilter
                :filtered-count="totalCount"
                :value="selectedCounselor"
                @counselor-change="handleCounselorChange"
            />

            <!-- 메인 테이블 -->
            <TodoTable 
                :data="todoData"
                :is-loading="isLoading"
                :error="error"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                :tab-type="activeTab"
                @sort="handleSort"
                @member-view="handleMemberView"
                @member-cs="handleMemberCs"
                @retry="handleRetry"
            />

            <!-- 페이징 -->
            <TodoPagination 
                v-if="showPagination"
                :current-page="currentPage"
                :total-pages="totalPages"
                :total-count="totalCount"
                :page-size="pageSize"
                :is-loading="isLoading"
                @page-change="handlePageChange"
                @page-size-change="handlePageSizeChange"
            />
        </vx-card>
    </div>
</template>

<script>
// Components
import TodoTabNavigation from '@/components/cs/TodoTabNavigation.vue';
import DateRangeNavigation from '@/components/cs/DateRangeNavigation.vue';
import CounselorFilter from '@/components/cs/CounselorFilter.vue';
import TodoTable from '@/components/cs/TodoTable.vue';
import TodoPagination from '@/components/cs/TodoPagination.vue';

// Services
import todoService from '@/services/todoService';

// Utils
import dateClass from '@/../util/dateClass';
import { EventBus } from '../../../util/vue-event-bus';

export default {
    name: 'TodoOptimized',
    components: {
        TodoTabNavigation,
        DateRangeNavigation,
        CounselorFilter,
        TodoTable,
        TodoPagination
    },
    data() {
        return {
            width: window.innerWidth,
            activeTab: 1,
            
            // Todo 데이터
            todoData: [],
            isLoading: false,
            error: null,
            
            // 페이징
            pagination: {
                currentPage: 1,
                pageSize: 50,
                totalCount: 0,
                totalPages: 0
            },
            
            // 정렬
            sorting: {
                orderBy: 'NEXT_DATE',
                orderDir: 'ASC'
            },
            
            // 필터
            filters: {
                tabType: 1,
                date: dateClass.dateYYYYMMDD(),
                searchKeyword: '',
                counselor: 'none'
            },
            
            // 날짜 네비게이션
            currentDate: dateClass.dateYYYYMMDD(),
            displayDateRange: '',
            isToday: true,
            
            // 상담 담당자 관련
            selectedCounselor: 'none'
        };
    },
    computed: {
        showDateNavigation() {
            return ![4, 5].includes(this.activeTab); // 이슈Todo, VIP 탭에서는 숨김
        },
        
        showPagination() {
            // 디버깅: 항상 페이징을 표시하도록 수정
            console.log('showPagination 체크:', { 
                totalPages: this.pagination.totalPages, 
                totalCount: this.pagination.totalCount,
                hasData: this.todoData.length > 0,
                dataLength: this.todoData.length
            });
            // 데이터가 있으면 항상 페이징 표시 (임시로 항상 true)
            return this.todoData.length > 0 || this.pagination.totalCount > 0;
        },
        
        
        currentPage() {
            return this.pagination.currentPage;
        },
        
        totalPages() {
            return this.pagination.totalPages;
        },
        
        totalCount() {
            return this.pagination.totalCount;
        },
        
        pageSize() {
            return this.pagination.pageSize;
        },
        
        sortField() {
            return this.sorting.orderBy;
        },
        
        sortDirection() {
            return this.sorting.orderDir;
        }
    },
    methods: {
        handleResize() {
            this.width = window.innerWidth;
        },
        
        async fetchTodoList(resetPage = true) {
            console.log('fetchTodoList 호출됨:', { 
                resetPage, 
                currentPageBefore: this.pagination.currentPage, 
                filters: this.filters,
                activeTab: this.activeTab 
            });
            
            if (resetPage) {
                console.log('🔄 페이지 리셋: 1페이지로 이동');
                this.pagination.currentPage = 1;
            } else {
                console.log('📄 페이지 유지:', this.pagination.currentPage);
            }

            this.isLoading = true;
            this.error = null;

            try {
                const params = {
                    tab: this.filters.tabType,
                    date: this.filters.date,
                    page: this.pagination.currentPage,
                    pageSize: this.pagination.pageSize,
                    orderBy: this.sorting.orderBy,
                    orderDir: this.sorting.orderDir,
                    counselor: this.filters.counselor
                };

                console.log('API 호출 파라미터:', params);
                const data = await todoService.getTodoList(params);
                console.log('API 응답 데이터:', data);
                
                if (data.success) {
                    // 페이지 변경 시는 항상 새로운 데이터로 교체
                    this.todoData = data.list || [];
                    
                    // 페이지네이션 정보 설정 (기본값 보장)
                    if (data.pagination) {
                        this.pagination.totalCount = data.pagination.totalCount || this.todoData.length;
                        this.pagination.totalPages = data.pagination.totalPages || Math.max(1, Math.ceil(this.pagination.totalCount / this.pagination.pageSize));
                        
                        // resetPage가 true이거나 서버에서 다른 페이지를 반환한 경우에만 currentPage 업데이트
                        if (resetPage || (data.pagination.currentPage && data.pagination.currentPage !== this.pagination.currentPage)) {
                            this.pagination.currentPage = data.pagination.currentPage || 1;
                            console.log('📄 서버에서 currentPage 업데이트:', this.pagination.currentPage);
                        } else {
                            console.log('📄 클라이언트 currentPage 유지:', this.pagination.currentPage);
                        }
                    } else {
                        // 페이지네이션 정보가 없는 경우 기본값 설정
                        this.pagination.totalCount = this.todoData.length;
                        this.pagination.totalPages = Math.max(1, Math.ceil(this.todoData.length / this.pagination.pageSize));
                        if (resetPage) {
                            this.pagination.currentPage = 1;
                        }
                    }
                    
                    console.log('최종 todoData:', this.todoData);
                    console.log('페이지 정보:', this.pagination);
                } else {
                    throw new Error('데이터 조회에 실패했습니다.');
                }
            } catch (err) {
                console.error('TODO 리스트 조회 오류:', err);
                this.error = err.message || '데이터 조회 중 오류가 발생했습니다.';
                this.todoData = [];
            } finally {
                this.isLoading = false;
            }
        },
        
        async handleTabChange(tabIndex) {
            if (this.activeTab !== tabIndex) {
                this.activeTab = tabIndex;
                this.filters.tabType = tabIndex;
                await this.fetchTodoList(true);
            }
        },
        
        handleSort(field) {
            console.log('🔄 프론트엔드 정렬 시작:', field);
            
            if (this.sorting.orderBy === field) {
                // 같은 필드 클릭 시 방향 토글
                this.sorting.orderDir = this.sorting.orderDir === 'ASC' ? 'DESC' : 'ASC';
            } else {
                // 다른 필드 클릭 시 ASC로 시작
                this.sorting.orderBy = field;
                this.sorting.orderDir = 'ASC';
            }
            
            console.log('🔄 정렬 설정:', { field, direction: this.sorting.orderDir });
            
            // 프론트엔드에서 현재 데이터를 정렬
            this.sortTodoData();
        },
        
        sortTodoData() {
            if (!this.todoData || this.todoData.length === 0) {
                console.log('📄 정렬할 데이터 없음');
                return;
            }
            
            const field = this.sorting.orderBy;
            const direction = this.sorting.orderDir;
            
            console.log('📊 정렬 실행:', { field, direction, dataCount: this.todoData.length });
            
            this.todoData.sort((a, b) => {
                let valueA = a[field];
                let valueB = b[field];
                
                // null/undefined 처리
                if (valueA == null) valueA = '';
                if (valueB == null) valueB = '';
                
                // 날짜 필드 처리 (YYYY-MM-DD 형식)
                if (field.includes('DATE') || field.includes('_DT')) {
                    valueA = new Date(valueA || '1900-01-01');
                    valueB = new Date(valueB || '1900-01-01');
                }
                // 숫자 필드 처리
                else if (typeof valueA === 'string' && !isNaN(valueA)) {
                    valueA = parseFloat(valueA) || 0;
                    valueB = parseFloat(valueB) || 0;
                }
                // 문자열 필드 처리
                else {
                    valueA = String(valueA).toLowerCase();
                    valueB = String(valueB).toLowerCase();
                }
                
                let result = 0;
                if (valueA < valueB) result = -1;
                else if (valueA > valueB) result = 1;
                
                return direction === 'ASC' ? result : -result;
            });
            
            console.log('✅ 정렬 완료');
        },
        
        async handlePrevPeriod() {
            const currentDate = new Date(
                parseInt(this.currentDate.substr(0, 4)),
                parseInt(this.currentDate.substr(4, 2)) - 1,
                parseInt(this.currentDate.substr(6, 2))
            );
            currentDate.setDate(currentDate.getDate() - 7);
            
            const newDate = currentDate.getFullYear().toString() + 
                           (currentDate.getMonth() + 1).toString().padStart(2, '0') + 
                           currentDate.getDate().toString().padStart(2, '0');
            
            this.currentDate = newDate;
            this.filters.date = newDate;
            this.updateDateRange();
            this.checkIsToday();
            await this.fetchTodoList(true);
        },
        
        async handleNextPeriod() {
            const currentDate = new Date(
                parseInt(this.currentDate.substr(0, 4)),
                parseInt(this.currentDate.substr(4, 2)) - 1,
                parseInt(this.currentDate.substr(6, 2))
            );
            currentDate.setDate(currentDate.getDate() + 7);
            
            const newDate = currentDate.getFullYear().toString() + 
                           (currentDate.getMonth() + 1).toString().padStart(2, '0') + 
                           currentDate.getDate().toString().padStart(2, '0');
            
            this.currentDate = newDate;
            this.filters.date = newDate;
            this.updateDateRange();
            this.checkIsToday();
            await this.fetchTodoList(true);
        },
        
        async handleGoToday() {
            const today = dateClass.dateYYYYMMDD();
            this.currentDate = today;
            this.filters.date = today;
            this.updateDateRange();
            this.checkIsToday();
            await this.fetchTodoList(true);
        },
        
        updateDateRange() {
            // 주간 범위 표시 구현
            const startDate = new Date(
                parseInt(this.currentDate.substr(0, 4)),
                parseInt(this.currentDate.substr(4, 2)) - 1,
                parseInt(this.currentDate.substr(6, 2))
            );
            
            const endDate = new Date(startDate);
            endDate.setDate(endDate.getDate() + 6);
            
            this.displayDateRange = `${this.formatDate(startDate)} ~ ${this.formatDate(endDate)}`;
        },
        
        formatDate(date) {
            return `${date.getMonth() + 1}/${date.getDate()}`;
        },
        
        checkIsToday() {
            const today = dateClass.dateYYYYMMDD();
            this.isToday = this.currentDate === today;
        },
        
        handleMemberView(uid) {
            EventBus.$emit('memberView', uid);
        },
        
        handleMemberCs(uid) {
            EventBus.$emit('memberCs', uid);
        },
        
        async handleRetry() {
            try {
                await this.fetchTodoList(true);
            } catch (err) {
                console.error('Retry failed:', err);
            }
        },
        
        async handlePageChange(page) {
            console.log('🔥 handlePageChange 시작 - 요청 페이지:', page, '현재 페이지:', this.pagination.currentPage, '총 페이지:', this.pagination.totalPages);
            
            if (page >= 1 && page <= this.pagination.totalPages && page !== this.pagination.currentPage) {
                console.log('✅ 유효한 페이지 변경 - 페이지 설정 시작:', page);
                
                // 이전 페이지 상태 저장
                const prevPage = this.pagination.currentPage;
                this.pagination.currentPage = page;
                
                console.log('📄 페이지 상태 변경:', prevPage, '→', this.pagination.currentPage);
                
                // 페이지 변경만 하는 경우이므로 resetPage = false
                console.log('🔄 fetchTodoList 호출 시작 (resetPage: false)');
                await this.fetchTodoList(false);
                console.log('🔄 fetchTodoList 호출 완료');
                
                console.log('✅ 페이지 변경 완료 - 최종 페이지:', this.pagination.currentPage);
            } else {
                console.log('❌ 유효하지 않은 페이지 변경 요청:', {
                    requestedPage: page,
                    currentPage: this.pagination.currentPage,
                    totalPages: this.pagination.totalPages,
                    isValidRange: page >= 1 && page <= this.pagination.totalPages,
                    isDifferent: page !== this.pagination.currentPage
                });
            }
        },
        
        async handlePageSizeChange(newPageSize) {
            this.pagination.pageSize = newPageSize;
            await this.fetchTodoList(true);
        },
        
        async handleCounselorChange(counselor) {
            console.log('상담 담당자 변경:', counselor);
            if (this.filters.counselor !== counselor) {
                this.filters.counselor = counselor;
                this.selectedCounselor = counselor;
                
                // 담당자 변경시 항상 데이터 로드
                await this.fetchTodoList(true);
            }
        }
    },
    
    async mounted() {
        console.log('TodoOptimized mounted');
        window.addEventListener('resize', this.handleResize);
        
        // 날짜 초기화 확인
        console.log('초기 날짜 설정:', this.filters.date, this.currentDate);
        
        // 날짜 범위 초기화
        this.updateDateRange();
        this.checkIsToday();
        
        console.log('날짜 범위 초기화 후:', this.displayDateRange, this.isToday);
        
        // 하드코딩된 상담 담당자 목록 사용으로 빠른 로딩
        // updateCounselorList() 제거 - 더 이상 백엔드에서 목록을 가져오지 않음
        
        // 초기 데이터 로드 (담당자 선택과 무관하게)
        console.log('초기 데이터 로드 시작');
        await this.fetchTodoList(true);

        // EventBus 리스너 등록
        EventBus.$on('remoteListCsRefresh', async () => {
            await this.handleRetry();
        });
        
        console.log('TodoOptimized 초기화 완료');
    },
    
    beforeDestroy() {
        window.removeEventListener('resize', this.handleResize);
        EventBus.$off('remoteListCsRefresh');
    }
};
</script>

<style scoped>
/* 기존 스타일 유지 */
</style>